/**
 * The made-to-measure elements (buttons, linings, collars...), filtered by
 * type. Admin only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCircleNotch,
  faPencilAlt,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
  DMS_ELEMENT_TYPES,
  MTM_ROUTE_PATH,
  PLATFORM,
  ROLES,
} from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { snackbar } from '@/shared/ui/snackbar';
import { useDeleteItem, useSavedDialogClose } from '../use-referential';
import plusIcon from '@/assets/images/plus.svg';
import { deleteDmsItem, dmsItemsQueryKey, dmsItemsQueryOptions } from '../api';
import { displayAvailability, nextListSort } from '../list';
import {
  displayedElements,
  elementDataSchema,
  isProtectedElement,
  type DmsElement,
  type ElementSort,
  type ElementSortField,
} from './element-form';
import { ElementDialog } from './ElementDialog';
import { Select } from '@/shared/ui/Select';
import { optionsOf } from '@/shared/ui/select-options';

const MIN_ROWS = 5;

const COLUMNS: {
  field: ElementSortField;
  label: string;
  width: string;
  testId: string;
}[] = [
  { field: 'type', label: 'Element Type', width: '25%', testId: 'type' },
  { field: 'name', label: 'Element Name', width: '25%', testId: 'name' },
  { field: 'codeBu', label: 'Code BU', width: '10%', testId: 'code-bu' },
  {
    field: 'displayAvailability',
    label: 'Availability',
    width: '10%',
    testId: 'availability',
  },
  { field: 'order', label: 'Order', width: '10%', testId: 'order' },
];

export function ElementsPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate(`/${MTM_ROUTE_PATH}/main`);
  }, [user, isAdmin]);

  const elements = useQuery({
    ...dmsItemsQueryOptions('element', elementDataSchema),
    enabled: isAdmin,
  });
  useEffect(() => {
    if (elements.isError)
      snackbar('Failed to load items, please try again later');
  }, [elements.isError]);

  const [type, setType] = useState<string | null>(null);
  const [sort, setSort] = useState<ElementSort>({ by: 'type', reverse: false });
  const [dialog, setDialog] = useState<{ item: DmsElement | null } | null>(
    null,
  );
  const [removing, setRemoving] = useState<DmsElement | null>(null);

  const items = useMemo(() => elements.data ?? [], [elements.data]);
  const displayed = displayedElements(items, type, sort);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: dmsItemsQueryKey('element') });

  const remove = useDeleteItem(
    (item: DmsElement) => deleteDmsItem(item.id),
    refresh,
  );

  const closeDialog = useSavedDialogClose(() => setDialog(null), refresh);

  if (!isAdmin) return null;

  return (
    <div className="container summary">
      <TopMenu platform={PLATFORM.DMS} />
      <div className="summary-table mb-12 mt-4">
        <div className="relative mb-4 flex items-end justify-between">
          <div>
            <h2>Elements</h2>
            <div className="field field-selector" style={{ width: 150 }}>
              <Select
                aria-label="Filter by element type"
                testId="mtm-element-filter-type"
                placeholder="--Filter by element type--"
                options={optionsOf(DMS_ELEMENT_TYPES)}
                value={type ?? ''}
                onChange={(next) => setType(next || null)}
              />
            </div>
          </div>
          <button
            type="button"
            className="add-btn absolute-x-center flex items-center"
            data-testid="header-add-element"
            onClick={() => setDialog({ item: null })}
          >
            <img src={plusIcon} alt="" />
            <span className="pl-2">Add a new element</span>
          </button>
        </div>
        <div className="table-container">
          <div className="items-container" role="region" aria-label="Elements">
            <table className="table items">
              <thead>
                <tr>
                  {COLUMNS.map(({ field, label, width, testId }) => (
                    <SortableHeader
                      key={field}
                      label={label}
                      width={width}
                      testId={`mtm-element-header-${testId}`}
                      active={sort.by === field}
                      descending={sort.reverse}
                      onClick={() =>
                        setSort((current) => nextListSort(current, field))
                      }
                    />
                  ))}
                  <th style={{ width: '10%' }}>Picture</th>
                  <th style={{ width: '10%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((item) => (
                  <tr key={item.id} className="user-row">
                    <td
                      style={{ width: '25%' }}
                      title={item.data.type ?? ''}
                      data-testid="mtm-element-row-type"
                    >
                      {item.data.type}
                    </td>
                    <td
                      style={{ width: '25%' }}
                      title={item.data.name ?? ''}
                      data-testid="mtm-element-row-name"
                    >
                      {item.data.name}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      title={item.data.codeBu ?? ''}
                      data-testid="mtm-element-row-code-bu"
                    >
                      {item.data.codeBu}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      title={displayAvailability(item.data)}
                      data-testid="mtm-element-row-availability"
                    >
                      {displayAvailability(item.data)}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      title={String(item.data.order ?? '')}
                      data-testid="mtm-element-row-order"
                    >
                      {item.data.order}
                    </td>
                    <td
                      style={{ width: '10%' }}
                      data-testid="mtm-element-row-picture"
                    >
                      {item.data.picture && (
                        <FontAwesomeIcon
                          icon={faCheck}
                          aria-label="Picture set"
                        />
                      )}
                    </td>
                    <td style={{ width: '10%' }} className="edit-action">
                      <button
                        type="button"
                        className="action-icon"
                        aria-label={`Edit ${item.data.name ?? ''}`}
                        data-testid="mtm-element-row-edit-button"
                        onClick={() => setDialog({ item })}
                      >
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </button>
                      <button
                        type="button"
                        className="action-icon ml-2"
                        aria-label={`Remove ${item.data.name ?? ''}`}
                        data-testid="mtm-element-row-delete-button"
                        onClick={() => {
                          if (isProtectedElement(item))
                            snackbar(
                              'You cannot delete the villa dior finishings option',
                            );
                          else setRemoving(item);
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
                {Array.from(
                  { length: Math.max(0, MIN_ROWS - displayed.length) },
                  (_, index) => (
                    <tr key={`empty-${index}`} className="empty-row">
                      <td style={{ width: '25%' }} />
                      <td style={{ width: '25%' }} />
                      <td style={{ width: '10%' }} />
                      <td style={{ width: '10%' }} />
                      <td style={{ width: '10%' }} />
                      <td style={{ width: '10%' }} />
                      <td style={{ width: '10%' }} />
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {elements.isFetching && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="loading-icon"
          aria-label="Loading"
        />
      )}
      {dialog && (
        <ElementDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          type={type}
          onClose={closeDialog}
        />
      )}
      {removing !== null && (
        <ConfirmDialog
          title="Are you sure?"
          text="This action cannot be undone. This item will be deleted immediately."
          onConfirm={() => {
            if (removing) remove.mutate(removing);
            setRemoving(null);
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
